import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { ResponsiveImage } from "../image/ResponsiveImage";

export const ImageAltTextDialog = ({ open, onClose, onSave, block }) => {
  const t = useScrollytellTranslation();
  const [localDecorative, setLocalDecorative] = useState(
    Boolean(block?.isDecorative)
  );
  const [localAltText, setLocalAltText] = useState(block?.altText || "");

  useEffect(() => {
    if (open) {
      setLocalDecorative(Boolean(block?.isDecorative));
      setLocalAltText(block?.altText || "");
    }
  }, [open, block?.isDecorative, block?.altText]);

  const handleSave = () => {
    onSave({
      isDecorative: localDecorative,
      altText: localDecorative ? "" : localAltText,
    });
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
        {t(
          "ImageBlockAltTextDialogTitle",
          "Alternativ tekst og tilgjengelighet for bilde"
        )}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {block?.imagePath && (
          <Box
            sx={{
              width: "100%",
              height: 200,
              backgroundColor: "#0F172A",
              borderRadius: 1,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #334155",
              "& img": {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            }}
          >
            <ResponsiveImage
              mediaId={block.imagePath}
              alt={localDecorative ? "" : localAltText || "Forhåndsvisning"}
            />
          </Box>
        )}

        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            backgroundColor: "#f5f5f5",
            border: "1px solid #e0e0e0",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(localDecorative)}
                onChange={(e) => setLocalDecorative(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontWeight: 500, fontSize: "0.95rem" }}>
                {t(
                  "ImageBlockDecorativeSwitchLabel",
                  "Bildet er kun dekorativt (skjules for skjermleser)"
                )}
              </Typography>
            }
          />
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, pl: 6 }}
          >
            {t(
              "ImageBlockDecorativeHelp",
              "Slå på denne hvis bildet kun er til pynt og ikke formidler faglig innhold. Da kreves ikke alternativ tekst etter WCAG 1.1.1."
            )}
          </Typography>
        </Box>

        {!localDecorative && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            inputProps={{ "data-a11y-field": "altText" }}
            label={t(
              "altTextLabel",
              "Alternativ tekst (for synshemmede / skjermleser)"
            )}
            helperText={t(
              "altTextHelper",
              "Beskriv hva motivet viser uten å skrive 'bilde av'."
            )}
            value={localAltText}
            onChange={(e) => setLocalAltText(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t("ImageBlockAltTextCancel", "Avbryt")}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("DialogApplyButton", "Bruk")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
